import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Y from 'yjs'
import { getCanvasChangeHints, type CanvasObservedNodeHint } from '../../api/client'
import { isApiRecord } from '../../api/responseDecoders'
import { createNadDebug } from '../../utils/debugLog'

const attributionLog = createNadDebug('canvas-observed-by')

const LABELS: Record<CanvasObservedNodeHint['last_observed_by'], string> = {
  'user session': 'observedBy.user',
  director: 'observedBy.director',
  agent: 'observedBy.agent',
  'worker-result': 'observedBy.workerResult',
  system: 'observedBy.system',
  unknown: 'observedBy.unknown',
}

async function matchesLiveField(
  hint: CanvasObservedNodeHint,
  data: object | undefined,
  position: { x: number; y: number } | undefined,
): Promise<boolean> {
  if (!hint.field || !hint.digest || !globalThis.crypto?.subtle) return false
  const value = (hint.field === 'x' || hint.field === 'y')
    ? position?.[hint.field]
    : isApiRecord(data) ? data[hint.field] : undefined
  // The server digests typed JSON. Complex Yjs/React projections can differ;
  // never claim a role for them without a verified canonical projection.
  if (value === undefined || (value !== null && typeof value === 'object')) return false
  const canonical = JSON.stringify(value)
  if (canonical === undefined || canonical.length > 65536) return false
  const bytes = new TextEncoder().encode(canonical)
  const digestOf = async (input: Uint8Array): Promise<string> => {
    const hash = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', input))
    return Array.from(hash, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  if (await digestOf(bytes) === hint.digest) return true
  // Python serializes a Yjs float 1.0 as "1.0"; JS serializes the same
  // numeric value as "1", including on editable input fields. Test both
  // scalar encodings without claiming a role for a different value.
  return typeof value === 'number'
    && Number.isInteger(value)
    && await digestOf(new TextEncoder().encode(canonical + '.0')) === hint.digest
}

/** Advisory hint for the selected node; missing/stale evidence is not a user edit. */
export function CanvasObservedByBadge({
  projectId,
  canvasId,
  nodeId,
  nodeVersion,
  nodePosition,
  ydoc,
}: {
  projectId: string
  canvasId: string
  nodeId: string
  nodeVersion?: object
  nodePosition?: { x: number; y: number }
  ydoc?: Y.Doc | null
}) {
  const { t } = useTranslation('canvas')
  const [hint, setHint] = useState<CanvasObservedNodeHint | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [hintVersion, setHintVersion] = useState<object | undefined>(undefined)
  const [hintPosition, setHintPosition] = useState<typeof nodePosition>(undefined)
  const disabledForSelection = useRef(false)
  const currentNodeVersion = useRef(nodeVersion)
  currentNodeVersion.current = nodeVersion
  const currentPosition = useRef(nodePosition)
  currentPosition.current = nodePosition
  const refreshCurrent = useRef<() => void>(() => {})
  const observedFirstVersion = useRef(false)

  useEffect(() => {
    disabledForSelection.current = false
  }, [projectId, canvasId, nodeId])

  useEffect(() => {
    let active = true
    if (disabledForSelection.current) return () => { active = false }
    setHint(null)
    let timer: number | undefined
    const refresh = () => {
      const requestedVersion = currentNodeVersion.current
      const requestedPosition = currentPosition.current
      const vector = ydoc ? Y.encodeStateVector(ydoc) : new Uint8Array([0])
      const deletes = ydoc ? Y.encodeStateAsUpdate(ydoc, vector) : new Uint8Array([0, 0])
      const encode = (bytes: Uint8Array) => btoa(Array.from(bytes, (byte) => String.fromCharCode(byte)).join(''))
      getCanvasChangeHints(projectId, canvasId, nodeId, encode(vector), encode(deletes))
        .then(async (response) => {
          if (!active) return
          setEnabled(response.enabled)
          if (!response.enabled) {
            disabledForSelection.current = true
            setHint(null)
            if (timer !== undefined) window.clearInterval(timer)
            return
          }
          const next = response.nodes.find((node) => node.node_id === nodeId) ?? null
          const verified = next !== null && await matchesLiveField(
            next, requestedVersion, requestedPosition,
          )
          if (!active) return
          if (active && currentNodeVersion.current === requestedVersion
            && currentPosition.current === requestedPosition) {
            setHint(verified ? next : null)
            setHintVersion(requestedVersion)
            setHintPosition(requestedPosition)
          }
          attributionLog({ event: 'selected-node-hint-read', node_id: nodeId, known: verified })
          if (timer === undefined) timer = window.setInterval(refresh, 10_000)
        })
        .catch(() => {
          if (active) setHint(null)
          attributionLog({ event: 'selected-node-hint-unavailable', node_id: nodeId })
          if (active && timer === undefined) timer = window.setInterval(refresh, 10_000)
        })
    }
    refreshCurrent.current = refresh
    refresh()
    return () => {
      active = false
      if (timer !== undefined) window.clearInterval(timer)
    }
  }, [projectId, canvasId, nodeId, ydoc])

  useEffect(() => {
    if (!observedFirstVersion.current) {
      observedFirstVersion.current = true
      return
    }
    if (disabledForSelection.current) return
    // Unknown is shown immediately by hintVersion mismatch. Debounce writes
    // (typing, progress ticks, Yjs rebuilds) to avoid one GET per update.
    const timeout = window.setTimeout(() => refreshCurrent.current(), 350)
    return () => window.clearTimeout(timeout)
  }, [nodeVersion, nodePosition])

  if (!enabled) return null
  const currentHint = hintVersion === nodeVersion && hintPosition === nodePosition ? hint : null
  const label = t(LABELS[currentHint?.last_observed_by ?? 'unknown'])
  return (
    <div
      className="rounded-lg border border-border bg-surface-1 px-3 py-2 text-xs font-medium text-muted-foreground shadow-lg"
      title={t('observedBy.disclaimer')}
      data-testid="canvas-observed-by"
    >
      {t('observedBy.lastChange')}: <span className="text-foreground">{label}</span>
    </div>
  )
}
